/**
 * BillingAdmin — tenant-admin billing surface.
 *
 *   - Show current subscription tier + status + period end + cancel-at-period-end flag
 *   - Surface tier upgrade options that hit /api/billing/checkout (Stripe)
 *   - Read post-checkout status from URL query params (?status=ok|canceled)
 *
 * Per Handoff §3 (locked pricing) + §12 final pre-launch checklist.
 */

import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSession } from "../lib/useSession.js";
// supabase client previously imported for direct tenant_subscriptions select;
// replaced by /api/billing/status (resolves env-configured grace windows
// server-side and returns precomputed countdown).
import {
  SectionHeader, Callout, NumberedList,
} from "../components/index.js";

const TIERS = [
  {
    id: "recruiter", label: "Core",
    price: "$49 / seat / mo (annual)",
    audience: "Solo operator getting started",
    bullets: [
      "Counterpart Breakdown — card + full briefing",
      "Pre-Meeting Briefs + Playbooks",
      "1 Relationship Read / day",
      "Up to 25 people",
    ],
  },
  {
    id: "manager", label: "Pro",
    price: "$149 / seat / mo (annual)",
    audience: "The working operator — sales, advisory, negotiation",
    bullets: [
      "All Core features",
      "Thought Engineering — strategy & design",
      "Negotiation Blueprint + Common Ground",
      "15 Room Blueprints / month",
      "Up to 75 people",
    ],
  },
  {
    id: "hr_director", label: "Advanced",
    price: "$349 / seat / mo (annual)",
    audience: "The operator running a full book of relationships",
    bullets: [
      "All Pro features (unlimited)",
      "Consistency audit surface",
      "Self-view share option",
      "On-behalf-of generation",
      "Profile sharing org-wide",
      "Multi-room comparison (3 compositions)",
    ],
  },
  {
    id: "enterprise", label: "Enterprise / White-label",
    price: "From $5,000 / yr custom",
    audience: "Firms installing the Behavioral Revenue System",
    bullets: [
      "Everything in Advanced",
      "Branded portal + custom domain",
      "Per-tenant theme overrides",
      "Custom Playbook section templates",
      "Partner-scoped audit isolation",
      "Multi-tenant",
    ],
  },
];

export default function BillingAdminPage() {
  const { session, appUser, loading } = useSession();
  const [params] = useSearchParams();
  const [subscription, setSubscription] = useState(null);
  const [seatCount, setSeatCount] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [graceCountdown, setGraceCountdown] = useState(null);
  const [graceWindows, setGraceWindows] = useState(null);

  const callbackStatus = params.get("status");

  useEffect(() => {
    if (!session) return;

    // /api/billing/status replaces the direct supabase select + the locked
    // frontend grace-window constants. The endpoint resolves the configured
    // env values (or documented defaults) server-side and returns a precomputed
    // grace_countdown — UI no longer drifts when env vars are overridden.
    fetch("/.netlify/functions/billing-status", {
      headers: { authorization: `Bearer ${session.access_token}` },
    })
      .then(async (r) => {
        const body = await r.json();
        if (!r.ok) {
          setError(body.error || "Could not load billing status");
          return;
        }
        setSubscription(body.subscription);
        setGraceCountdown(body.grace_countdown);
        setGraceWindows(body.grace_windows);
      })
      .catch((e) => setError(e.message));

    // Fetch recent invoices alongside the subscription
    fetch("/.netlify/functions/billing-invoices", {
      headers: { authorization: `Bearer ${session.access_token}` },
    })
      .then((r) => r.json())
      .then((b) => { if (b.invoices) setInvoices(b.invoices); })
      .catch(() => {});
  }, [session, callbackStatus]);

  const openPortal = async () => {
    setOpeningPortal(true);
    setError(null);
    try {
      const res = await fetch("/.netlify/functions/billing-portal", {
        method: "POST",
        headers: { authorization: `Bearer ${session.access_token}` },
      });
      const body = await res.json();
      if (!res.ok) {
        setError(body.error || "Portal open failed");
        setOpeningPortal(false);
        return;
      }
      window.location.href = body.portal_url;
    } catch (e) {
      setError(e.message);
      setOpeningPortal(false);
    }
  };

  const checkout = async (tier) => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch("/.netlify/functions/billing-checkout", {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ tier, seat_count: seatCount }),
      });
      const body = await res.json();
      if (!res.ok) {
        setError(body.error || "Checkout failed");
        setBusy(false);
        return;
      }
      window.location.href = body.checkout_url;
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  if (loading) return <p style={{ padding: 24 }}>Loading…</p>;
  if (!session) return <p style={{ padding: 24 }}>Sign in to manage billing.</p>;
  if (!appUser?.is_tenant_admin) {
    return (
      <div style={{ maxWidth: 540, margin: "60px auto", padding: "0 24px" }}>
        <h1 style={titleStyle}>Tenant admin access required</h1>
        <p style={paragraphStyle}>
          Billing changes require tenant admin access. Contact your tenant admin to request a tier change.
        </p>
      </div>
    );
  }

  const currentTier = subscription?.tier || appUser?.tier || "manager";

  return (
    <div style={{ maxWidth: 920, margin: "0 auto", padding: "32px 24px" }}>
      <SectionHeader index={1} title="Billing" />

      {callbackStatus === "ok" && (
        <Callout.Tip>
          <strong>Subscription updated.</strong> The tier change applies immediately to all members of this tenant.
        </Callout.Tip>
      )}
      {callbackStatus === "canceled" && (
        <Callout.Remember>
          Checkout canceled. No change was made.
        </Callout.Remember>
      )}

      <CurrentSubscriptionCard
        subscription={subscription}
        appUserTier={appUser?.tier}
        graceCountdown={graceCountdown}
        graceWindows={graceWindows}
      />

      {subscription && (
        <div style={{ marginBottom: 24 }}>
          <button onClick={openPortal} disabled={openingPortal} style={{
            padding: "10px 16px",
            fontFamily: "var(--ppr-typography-font-family-display)",
            fontSize: "var(--ppr-typography-font-size-sm)",
            letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
            textTransform: "uppercase",
            border: "1px solid var(--ppr-color-border-default)",
            background: "var(--ppr-color-background-surface)",
            color: "var(--ppr-color-accent-navy)",
            cursor: "pointer",
          }}>
            {openingPortal ? "Opening…" : "Manage payment methods + cancel"}
          </button>
        </div>
      )}

      {error && (
        <div style={errorStyle}>
          {error}
        </div>
      )}

      <SectionHeader index={2} title="Tiers" />

      <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}>
        {TIERS.map((t) => (
          <TierCard
            key={t.id}
            tier={t}
            isCurrent={t.id === currentTier}
            onSelect={() => checkout(t.id)}
            busy={busy}
          />
        ))}
      </div>

      {invoices.length > 0 && <InvoicesSection invoices={invoices} bearerToken={session.access_token} />}

      {/* Stripe Customer-Portal branding config + "Powered by Stripe" footer
          removed 2026-06-10 — premature enterprise/white-label billing
          scaffolding (no live Stripe subscriptions yet). The
          PortalConfigurationSection component is retained below, unrendered,
          so it can be restored when Stripe billing is actually wired. */}
    </div>
  );
}

function PortalConfigurationSection({ bearerToken, tier, subscriptionTier }) {
  const isEnterprise = tier === "enterprise" || subscriptionTier === "enterprise";
  const [cfgId, setCfgId] = useState("");
  const [original, setOriginal] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    if (!isEnterprise || !bearerToken) return;
    fetch("/.netlify/functions/tenant-portal-config", {
      headers: { authorization: `Bearer ${bearerToken}` },
    })
      .then(async (r) => {
        const body = await r.json();
        if (!r.ok) {
          setError(body.error || "Could not load portal configuration");
          return;
        }
        setOriginal(body.stripe_portal_configuration_id || "");
        setCfgId(body.stripe_portal_configuration_id || "");
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoaded(true));
  }, [bearerToken, isEnterprise]);

  if (!isEnterprise) return null;

  const dirty = (cfgId.trim() || null) !== (original || null);

  const save = async () => {
    setSaving(true);
    setError(null);
    setSavedAt(null);
    try {
      const res = await fetch("/.netlify/functions/tenant-portal-config", {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${bearerToken}` },
        body: JSON.stringify({ stripe_portal_configuration_id: cfgId.trim() || null }),
      });
      const body = await res.json();
      if (!res.ok) {
        setError(body.error || "Save failed");
        return;
      }
      setOriginal(body.stripe_portal_configuration_id || "");
      setCfgId(body.stripe_portal_configuration_id || "");
      setSavedAt(new Date());
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ marginTop: 32 }}>
      <SectionHeader index="∗" title="Customer portal branding (Enterprise)" />
      <p style={{
        fontFamily: "var(--ppr-typography-font-family-body)",
        fontSize: "var(--ppr-typography-font-size-base)",
        color: "var(--ppr-color-text-secondary)",
        lineHeight: "var(--ppr-typography-line-height-relaxed)",
        margin: "8px 0 14px",
      }}>
        Stripe's hosted Customer Portal carries Stripe's default branding unless you create your own Configuration in the Stripe dashboard and paste its id below. Find it under <strong>Stripe → Settings → Billing → Customer portal → Configurations</strong>. Configuration ids start with <code>bpc_</code>.
      </p>

      <div style={{ display: "flex", gap: 12, alignItems: "stretch", flexWrap: "wrap" }}>
        <input
          type="text"
          value={cfgId}
          onChange={(e) => setCfgId(e.target.value)}
          placeholder="bpc_…"
          disabled={!loaded || saving}
          style={{
            flex: "1 1 320px",
            padding: "10px 14px",
            fontFamily: "var(--ppr-typography-font-family-mono)",
            fontSize: "13px",
            border: "1px solid var(--ppr-color-border-default)",
            background: "var(--ppr-color-background-surface)",
            color: "var(--ppr-color-text-primary)",
            outline: "none",
          }}
        />
        <button
          onClick={save}
          disabled={!dirty || saving}
          style={{
            padding: "10px 16px",
            fontFamily: "var(--ppr-typography-font-family-display)",
            fontSize: "var(--ppr-typography-font-size-sm)",
            letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
            textTransform: "uppercase",
            border: "1px solid var(--ppr-color-border-default)",
            background: dirty ? "var(--ppr-color-accent-navy)" : "var(--ppr-color-background-surface)",
            color: dirty ? "var(--ppr-color-text-inverse)" : "var(--ppr-color-accent-navy)",
            cursor: dirty && !saving ? "pointer" : "not-allowed",
            opacity: saving ? 0.7 : 1,
          }}
        >
          {saving ? "Saving…" : cfgId.trim() ? "Save" : "Clear"}
        </button>
      </div>

      {error && <div style={errorStyle}>{error}</div>}
      {savedAt && (
        <p style={{
          marginTop: 8,
          fontFamily: "var(--ppr-typography-font-family-mono)",
          fontSize: "11px",
          color: "var(--ppr-color-text-muted)",
        }}>
          Saved {savedAt.toLocaleTimeString()} — applies to the next portal session your tenant opens.
        </p>
      )}
    </div>
  );
}

function InvoicesSection({ invoices, bearerToken }) {
  const [downloading, setDownloading] = useState(false);

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const res = await fetch("/.netlify/functions/billing-invoices?format=csv&limit=100", {
        headers: { authorization: `Bearer ${bearerToken}` },
      });
      if (!res.ok) {
        // Surface error inline; CSV should be a one-shot success or the
        // admin needs to know why
        return;
      }
      const blob = await res.blob();
      const filename = parseFilenameFromContentDisposition(res.headers.get("content-disposition"))
        || `tpm26-invoices-${new Date().toISOString().slice(0, 10)}.csv`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div style={{ marginTop: 32 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <SectionHeader index={3} title="Invoices" />
        <button
          onClick={downloadCsv}
          disabled={downloading}
          style={{
            padding: "8px 14px",
            fontFamily: "var(--ppr-typography-font-family-display)",
            fontSize: "11px",
            letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
            textTransform: "uppercase",
            border: "1px solid var(--ppr-color-border-default)",
            background: "var(--ppr-color-background-surface)",
            color: "var(--ppr-color-accent-navy)",
            cursor: downloading ? "wait" : "pointer",
            opacity: downloading ? 0.7 : 1,
          }}
        >
          {downloading ? "Preparing…" : "Download CSV (up to 100)"}
        </button>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {invoices.map((inv) => (
          <article key={inv.id} style={{
            padding: "12px 14px",
            border: "1px solid var(--ppr-color-border-default)",
            background: "var(--ppr-color-background-surface)",
            display: "grid",
            gridTemplateColumns: "1.5fr 1fr 1fr 1fr auto",
            gap: 14,
            alignItems: "center",
          }}>
            <div>
              <div style={{ fontFamily: "var(--ppr-typography-font-family-display)", fontSize: "13px", color: "var(--ppr-color-accent-navy)" }}>
                {inv.number || inv.id.slice(0, 12)}
              </div>
              <div style={{ fontFamily: "var(--ppr-typography-font-family-mono)", fontSize: "10px", color: "var(--ppr-color-text-muted)" }}>
                {inv.created_at ? new Date(inv.created_at).toLocaleDateString() : ""}
              </div>
            </div>
            <div style={{ fontFamily: "var(--ppr-typography-font-family-body)", fontSize: "13px" }}>
              {formatMoney(inv.total, inv.currency)}
            </div>
            <StatusPill status={inv.status} />
            <div style={{ fontFamily: "var(--ppr-typography-font-family-mono)", fontSize: "11px", color: "var(--ppr-color-text-secondary)" }}>
              {inv.period_start && inv.period_end
                ? `${new Date(inv.period_start).toLocaleDateString()} → ${new Date(inv.period_end).toLocaleDateString()}`
                : ""}
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              {inv.hosted_invoice_url && <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer" style={linkBtnStyle}>View</a>}
              {inv.invoice_pdf && <a href={inv.invoice_pdf} target="_blank" rel="noreferrer" style={linkBtnStyle}>PDF</a>}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

function parseFilenameFromContentDisposition(header) {
  if (!header) return null;
  const m = header.match(/filename="([^"]+)"/);
  return m ? m[1] : null;
}

function StatusPill({ status }) {
  const styles = {
    paid:  { bg: "var(--ppr-color-accent-pale-soft)", fg: "var(--ppr-color-accent-navy)" },
    open:  { bg: "var(--ppr-color-accent-coral-very-soft)", fg: "var(--ppr-color-accent-coral-dark)" },
    void:  { bg: "var(--ppr-color-background-muted)", fg: "var(--ppr-color-text-muted)" },
    draft: { bg: "var(--ppr-color-background-muted)", fg: "var(--ppr-color-text-secondary)" },
  }[status] || { bg: "var(--ppr-color-background-muted)", fg: "var(--ppr-color-text-muted)" };
  return (
    <span style={{
      padding: "4px 10px",
      background: styles.bg,
      color: styles.fg,
      fontFamily: "var(--ppr-typography-font-family-display)",
      fontSize: "10px",
      letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
      textTransform: "uppercase",
      textAlign: "center",
    }}>
      {status}
    </span>
  );
}

function formatMoney(cents, currency) {
  if (cents == null) return "—";
  const amt = (cents / 100).toFixed(2);
  return `${(currency || "usd").toUpperCase()} ${amt}`;
}

const linkBtnStyle = {
  padding: "6px 10px",
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: "10px",
  letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
  textTransform: "uppercase",
  border: "1px solid var(--ppr-color-border-default)",
  background: "var(--ppr-color-background-surface)",
  color: "var(--ppr-color-accent-navy)",
  textDecoration: "none",
};

// Grace-period countdown is computed server-side by /api/billing/status —
// the endpoint resolves GRACE_DOWNGRADE_*_DAYS env vars or documented
// defaults, then computes days_remaining + fires_at_iso from
// subscription.updated_at. The UI consumes the precomputed values so it
// stays accurate when env vars are overridden in production.

function CurrentSubscriptionCard({ subscription, appUserTier, graceCountdown, graceWindows }) {
  if (!subscription) {
    return (
      <Callout.Tip>
        <strong>No active subscription.</strong> Your tenant is currently on the <strong>{appUserTier || "manager"}</strong> tier (default tier from tenant creation). Pick a tier below to start a paid subscription.
      </Callout.Tip>
    );
  }
  return (
    <>
      <div style={subscriptionCardStyle}>
        <div style={{
          fontFamily: "var(--ppr-typography-font-family-display)",
          fontSize: "11px",
          letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
          textTransform: "uppercase",
          color: "var(--ppr-color-accent-navy)",
          marginBottom: 4,
        }}>
          Current subscription
        </div>
        <div style={{
          fontFamily: "var(--ppr-typography-font-family-display)",
          fontSize: "var(--ppr-typography-font-size-2xl)",
          color: "var(--ppr-color-accent-navy)",
          marginBottom: 8,
        }}>
          {subscription.tier} · {subscription.status}
        </div>
        <div style={{ fontFamily: "var(--ppr-typography-font-family-mono)", fontSize: "12px", color: "var(--ppr-color-text-secondary)" }}>
          {subscription.seat_count} seat{subscription.seat_count === 1 ? "" : "s"}
          {subscription.current_period_end && ` · current period ends ${new Date(subscription.current_period_end).toLocaleDateString()}`}
          {subscription.cancel_at_period_end && " · CANCELING AT PERIOD END"}
          {subscription.trial_end && ` · trial ends ${new Date(subscription.trial_end).toLocaleDateString()}`}
        </div>
      </div>
      <GraceCountdownBanner
        subscription={subscription}
        graceCountdown={graceCountdown}
        graceWindows={graceWindows}
      />
    </>
  );
}

/**
 * Shows when the grace-period downgrade job will fire on a canceled or
 * past_due subscription. Consumes server-computed values from
 * /api/billing/status (V1.x ship 2026-05-04 — replaces the locked frontend
 * constants that previously could drift from configured env vars).
 */
function GraceCountdownBanner({ subscription, graceCountdown, graceWindows }) {
  if (!graceCountdown?.applies) return null;
  const status = subscription?.status;
  const days = graceCountdown.days_remaining;
  const firesAt = graceCountdown.fires_at_iso ? new Date(graceCountdown.fires_at_iso) : null;

  // If the server returned 'env' for source, the configured windows differ
  // from the documented defaults — surface this in the banner so admin
  // expectations match what the scheduled job will actually do.
  const isCustomWindow = graceWindows?.source === "env";

  const whenText = days > 0
    ? `in ~${days} day${days === 1 ? "" : "s"}${firesAt ? ` (around ${firesAt.toLocaleDateString()})` : ""}`
    : "on the next nightly job run";

  if (status === "canceled") {
    return (
      <Callout.WatchOut>
        <strong>Tier downgrade pending.</strong> This subscription is canceled. All members of this tenant will downgrade to the <strong>Core</strong> tier {whenText}. Re-subscribe to a paid tier below to keep the current tier.
        {isCustomWindow && (
          <span style={{ display: "block", marginTop: 6, fontSize: "11px", color: "var(--ppr-color-text-muted)" }}>
            (Configured grace window: {graceWindows.canceled_grace_days} days for canceled subscriptions, set via env var.)
          </span>
        )}
      </Callout.WatchOut>
    );
  }

  return (
    <Callout.WatchOut>
      <strong>Payment past due.</strong> Stripe is retrying. If the issue isn't resolved within {whenText}, all members of this tenant will downgrade to the <strong>Core</strong> tier. Update payment method via the Stripe portal below.
      {isCustomWindow && (
        <span style={{ display: "block", marginTop: 6, fontSize: "11px", color: "var(--ppr-color-text-muted)" }}>
          (Configured grace window: {graceWindows.past_due_grace_days} days for past-due subscriptions, set via env var.)
        </span>
      )}
    </Callout.WatchOut>
  );
}

function TierCard({ tier, isCurrent, onSelect, busy }) {
  return (
    <div style={{
      padding: "18px 20px",
      border: isCurrent ? "2px solid var(--ppr-color-accent-coral)" : "1px solid var(--ppr-color-border-default)",
      background: "var(--ppr-color-background-surface)",
      display: "flex",
      flexDirection: "column",
      gap: 10,
    }}>
      <div style={{
        fontFamily: "var(--ppr-typography-font-family-display)",
        fontSize: "16px",
        color: "var(--ppr-color-accent-navy)",
        letterSpacing: "var(--ppr-typography-letter-spacing-snug)",
      }}>
        {tier.label}
      </div>
      <div style={{ fontFamily: "var(--ppr-typography-font-family-mono)", fontSize: "12px", color: "var(--ppr-color-text-secondary)" }}>
        {tier.price}
      </div>
      <div style={{ fontFamily: "var(--ppr-typography-font-family-body)", fontSize: "13px", color: "var(--ppr-color-text-secondary)", fontStyle: "italic" }}>
        {tier.audience}
      </div>
      <ul style={{ margin: "6px 0 8px", paddingLeft: 18, fontFamily: "var(--ppr-typography-font-family-body)", fontSize: "13px", lineHeight: "var(--ppr-typography-line-height-relaxed)" }}>
        {tier.bullets.map((b, i) => <li key={i} style={{ marginBottom: 4 }}>{b}</li>)}
      </ul>
      {isCurrent ? (
        <span style={{
          padding: "10px 14px",
          fontFamily: "var(--ppr-typography-font-family-display)",
          fontSize: "11px",
          letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
          textTransform: "uppercase",
          background: "var(--ppr-color-accent-coral-very-soft)",
          color: "var(--ppr-color-accent-coral-dark)",
          textAlign: "center",
          border: "1px dashed var(--ppr-color-accent-coral)",
        }}>
          Current tier
        </span>
      ) : (
        <button onClick={onSelect} disabled={busy} style={primaryBtn}>
          {busy ? "Redirecting…" : `Switch to ${tier.label}`}
        </button>
      )}
    </div>
  );
}

const titleStyle = {
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: "var(--ppr-typography-font-size-3xl)",
  color: "var(--ppr-color-accent-navy)",
  margin: "16px 0 12px",
};
const paragraphStyle = {
  fontFamily: "var(--ppr-typography-font-family-body)",
  fontSize: "var(--ppr-typography-font-size-base)",
  color: "var(--ppr-color-text-secondary)",
};
const subscriptionCardStyle = {
  padding: "18px 20px",
  border: "1px solid var(--ppr-color-border-default)",
  borderTop: "8px solid var(--ppr-color-accent-coral)",
  background: "var(--ppr-color-background-surface)",
  marginBottom: 24,
};
const primaryBtn = {
  padding: "10px 14px",
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: "var(--ppr-typography-font-size-sm)",
  letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
  textTransform: "uppercase",
  border: "1px solid var(--ppr-color-border-default)",
  background: "var(--ppr-color-accent-navy)",
  color: "var(--ppr-color-text-inverse)",
  cursor: "pointer",
};
const errorStyle = {
  padding: "12px 14px",
  border: "2px solid var(--ppr-color-accent-coral)",
  background: "var(--ppr-color-accent-coral-very-soft)",
  color: "var(--ppr-color-accent-coral-dark)",
  margin: "16px 0",
};
